import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Select from 'react-select';
import { 
  FaTrash, 
  FaPlus, 
  FaInfoCircle, 
  FaUserTie, 
  FaFileUpload,
  FaCheckCircle,
  FaExclamationTriangle,
  FaHandshake
} from 'react-icons/fa';
import { useLoader } from '../service/Hooks/useLoader';
import nonManagedAccountsService from '../service/api/nonManagedAccountsService';
import apiClient from '../service/api/api';
import useSelectedDomain from '../service/Hooks/useSelectedDomain';

// ========== STYLES REACT-SELECT PERSONNALISÉS ==========
const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '42px',
    borderColor: state.isFocused ? '#4b6cb7' : state.selectProps.error ? '#dc3545' : '#dee2e6',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(75, 108, 183, 0.25)' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? '#4b6cb7' : '#adb5bd'
    },
    transition: 'all 0.2s ease'
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected 
      ? '#4b6cb7' 
      : state.isFocused 
      ? '#e7f1ff' 
      : 'white',
    color: state.isSelected ? 'white' : '#212529',
    padding: '10px 12px',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#4b6cb7'
    }
  }),
  menu: (base) => ({
    ...base,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    borderRadius: '8px',
    overflow: 'hidden'
  }),
  menuList: (base) => ({
    ...base,
    padding: 0
  }),
  placeholder: (base) => ({
    ...base,
    color: '#6c757d',
    fontSize: '14px'
  }),
  singleValue: (base) => ({
    ...base,
    color: '#212529',
    fontWeight: '500'
  }),
  input: (base) => ({
    ...base,
    color: '#212529'
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? '#4b6cb7' : '#6c757d',
    transition: 'all 0.2s ease',
    '&:hover': {
      color: '#4b6cb7'
    }
  }),
  clearIndicator: (base) => ({
    ...base,
    color: '#6c757d',
    '&:hover': {
      color: '#dc3545'
    }
  }),
  loadingIndicator: (base) => ({
    ...base,
    color: '#4b6cb7'
  })
};

const AddLegalRepresentativesModal = ({
  account,
  accountId,
  onClose,
  onSuccess,
  show = false
}) => {
  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    setError,
    clearErrors
  } = useForm({
    defaultValues: {
      representatives: [
        {
          representativeAccountId: null,
          mandateType: '',
          relationshipType: '',
          otherMandateText: '',
          otherRelationshipText: '',
          mandateProofFile: null
        }
      ]
    }
  });

  const [nonManagedAccounts, setNonManagedAccounts] = useState([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [backendErrors, setBackendErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1); // Guide pas à pas
  const { showLoader, hideLoader } = useLoader();
  const selectedDomain = useSelectedDomain();

  // ========== OPTIONS ENRICHIES AVEC DESCRIPTIONS ==========
  const mandateTypeOptions = useMemo(() => ([
    { 
      value: 'CO_HOLDER', 
      label: 'Co-titulaire',
      description: 'Personne partageant la titularité du compte',
      icon: '🤝'
    },
    { 
      value: 'TUTORSHIP', 
      label: 'Tutelle',
      description: 'Mesure de protection juridique pour une personne majeure',
      icon: '👨‍⚖️'
    },
    { 
      value: 'CURATORSHIP', 
      label: 'Curatelle',
      description: 'Assistance pour les actes importants',
      icon: '🛡️'
    },
    { 
      value: 'POWER_OF_ATTORNEY', 
      label: 'Procuration',
      description: 'Autorisation d\'agir au nom du titulaire',
      icon: '📜'
    },
    { 
      value: 'LEGAL_GUARDIAN', 
      label: 'Représentant Légal',
      description: 'Représentation légale du titulaire',
      icon: '⚖️'
    },
    { 
      value: 'PARENT', 
      label: 'Parent',
      description: 'Parent d\'un mineur titulaire du compte',
      icon: '👨‍👩‍👧'
    },
    { 
      value: 'OTHER', 
      label: 'Autre',
      description: 'Autre type de mandat (à préciser)',
      icon: '✏️'
    }
  ]), []);

  const relationshipTypeOptions = useMemo(() => ([
    { 
      value: 'CO_HOLDER', 
      label: 'Co-titulaire',
      description: 'Titulaire conjoint du compte',
      icon: '🤝'
    },
    { 
      value: 'PARENT', 
      label: 'Parent',
      description: 'Lien de parenté direct',
      icon: '👨‍👩‍👧'
    },
    { 
      value: 'GUARDIAN', 
      label: 'Tuteur',
      description: 'Tuteur légal désigné',
      icon: '🛡️'
    },
    { 
      value: 'CURATOR', 
      label: 'Curateur',
      description: 'Curateur légal désigné',
      icon: '👨‍⚖️'
    },
    { 
      value: 'LEGAL_REPRESENTATIVE', 
      label: 'Représentant Légal',
      description: 'Représentant légalement désigné',
      icon: '⚖️'
    },
    { 
      value: 'ATTORNEY', 
      label: 'Mandataire',
      description: 'Personne mandatée par procuration',
      icon: '📜'
    },
    { 
      value: 'SPOUSE', 
      label: 'Conjoint',
      description: 'Époux ou épouse du titulaire',
      icon: '💍'
    },
    { 
      value: 'OTHER', 
      label: 'Autre',
      description: 'Autre type de relation (à préciser)',
      icon: '✏️'
    }
  ]), []);

  useEffect(() => {
    if (accountId && selectedDomain?.id) {
      fetchNonManagedAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, searchTerm, selectedDomain?.id]);

  const fetchNonManagedAccounts = async () => {
    if (!selectedDomain?.id) {
      toast.error('Aucun domaine sélectionné');
      return;
    }

    setIsLoadingAccounts(true);
    try {
      const options = await nonManagedAccountsService.getNonManagedAccounts(
        selectedDomain.id,
        { search: searchTerm, pageSize: 100 }
      );
      setNonManagedAccounts(options);
    } catch (error) {
      console.error('Erreur chargement comptes:', error);
      toast.error('Erreur lors du chargement des comptes');
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  const representatives = watch('representatives') || [];

  const addRepresentative = () => {
    clearErrors();

    setValue('representatives', [
      ...representatives,
      {
        representativeAccountId: null,
        mandateType: '',
        relationshipType: '',
        otherMandateText: '',
        otherRelationshipText: '',
        mandateProofFile: null
      }
    ]);

    toast.info('Nouveau mandataire ajouté');
  };

  const removeRepresentative = (index) => {
    clearErrors();

    if (representatives.length > 1) {
      const newRepresentatives = representatives.filter((_, i) => i !== index);
      setValue('representatives', newRepresentatives);
      toast.success('Mandataire supprimé');
    } else {
      toast.error('Au moins un mandataire est requis');
    }
  };

  const buildFinalValue = (typeValue, otherText) => {
    if (!typeValue) return '';
    if (typeValue !== 'OTHER') return typeValue;

    const clean = (otherText || '').trim();
    if (!clean) return 'OTHER';
    return `OTHER: ${clean}`;
  };

  const validateForm = () => {
    clearErrors();
    let ok = true;

    representatives.forEach((rep, index) => {
      if (!rep.representativeAccountId) {
        setError(`representatives.${index}.representativeAccountId`, {
          type: 'manual',
          message: 'Sélectionnez un compte mandataire'
        });
        ok = false;
      }

      if (!rep.mandateType) {
        setError(`representatives.${index}.mandateType`, {
          type: 'manual',
          message: 'Sélectionnez un type de mandat'
        });
        ok = false;
      }

      if (!rep.relationshipType) {
        setError(`representatives.${index}.relationshipType`, {
          type: 'manual',
          message: 'Sélectionnez une relation'
        });
        ok = false;
      }

      if (rep.mandateType === 'OTHER' && !rep.otherMandateText?.trim()) {
        setError(`representatives.${index}.otherMandateText`, {
          type: 'manual',
          message: 'Précisez le type de mandat'
        });
        ok = false;
      }

      if (rep.relationshipType === 'OTHER' && !rep.otherRelationshipText?.trim()) {
        setError(`representatives.${index}.otherRelationshipText`, {
          type: 'manual',
          message: 'Précisez la relation'
        });
        ok = false;
      }

      if (!rep.mandateProofFile) {
        setError(`representatives.${index}.mandateProofFile`, {
          type: 'manual',
          message: 'Document requis'
        });
        ok = false;
      }
    });

    return ok;
  };

  const onSubmit = async (data) => {
    setBackendErrors({});

    if (!validateForm()) {
      toast.error('Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    if (!accountId) {
      toast.error('ID du compte manquant');
      return;
    }

    if (!selectedDomain?.id) {
      toast.error('Domaine non sélectionné');
      return;
    }

    showLoader({
      message: 'Ajout des mandataires en cours...',
      indeterminate: true
    });

    try {
      const formData = new FormData();
      formData.append('AccountId', accountId.toString());
      formData.append('DomainId', selectedDomain.id.toString());

      data.representatives.forEach((rep) => {
        const finalMandateType = buildFinalValue(rep.mandateType, rep.otherMandateText);
        const finalRelationshipType = buildFinalValue(rep.relationshipType, rep.otherRelationshipText);

        formData.append('RepresentativeAccountIds', rep.representativeAccountId.toString());
        formData.append('MandateTypes', finalMandateType);
        formData.append('RelationshipTypes', finalRelationshipType);

        if (rep.mandateProofFile) {
          formData.append('MandateProofFiles', rep.mandateProofFile);
        }
      });

      const response = await apiClient.post(
        '/api/LegalRepresentatives/add-representative',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        const count = data.representatives.length;
        toast.success(
          count === 1
            ? '✅ Mandataire ajouté avec succès'
            : `✅ ${count} mandataires ajoutés avec succès`,
          {
            duration: 4000,
            icon: '🎉'
          }
        );

        onSuccess?.();
        onClose();
      } else {
        throw new Error(response.data.message || 'Erreur lors de l\'ajout');
      }

    } catch (error) {
      console.error('Erreur détaillée:', error);

      if (error.response?.data?.error) {
        const backendError = error.response.data.error;
        toast.error(`❌ ${backendError}`);
        setBackendErrors({ general: backendError });
      } else if (error.response?.data?.message) {
        toast.error(`❌ ${error.response.data.message}`);
        setBackendErrors({ general: error.response.data.message });
      } else {
        toast.error('❌ Erreur lors de l\'ajout des mandataires');
        setBackendErrors({ general: 'Erreur inconnue' });
      }
    } finally {
      hideLoader();
    }
  };

  // ========== CALCUL DE LA PROGRESSION ==========
  const calculateProgress = () => {
    const totalFields = representatives.length * 4; // 4 champs par mandataire
    let filledFields = 0;

    representatives.forEach(rep => {
      if (rep.representativeAccountId) filledFields++;
      if (rep.mandateType) filledFields++;
      if (rep.relationshipType) filledFields++;
      if (rep.mandateProofFile) filledFields++;
    });

    return Math.round((filledFields / totalFields) * 100);
  };

  const progress = calculateProgress();

  if (!show) return null;

  return (
    <div 
      className="modal fade show d-block" 
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px' }}>
          
          {/* ========== HEADER AMÉLIORÉ ========== */}
          <div 
            className="modal-header text-white position-relative" 
            style={{ 
              background: 'linear-gradient(135deg, #4b6cb7 0%, #182848 100%)',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
              padding: '1.5rem 2rem'
            }}
          >
            <div className="d-flex align-items-center gap-3 flex-grow-1">
              <div 
                className="d-flex align-items-center justify-content-center rounded-circle bg-white text-primary"
                style={{ width: '50px', height: '50px' }}
              >
                <FaUserTie size={24} />
              </div>
              <div>
                <h4 className="mb-0 fw-bold">Ajouter des Représentants ou Co-titulaires</h4>
                <small className="opacity-75">
                  Compte: {account?.companyName || `${account?.firstName || ''} ${account?.lastName || ''}`.trim() || 'Inconnu'}
                </small>
              </div>
            </div>
            
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
              aria-label="Close"
              style={{ 
                opacity: 0.8,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.opacity = 1}
              onMouseLeave={(e) => e.target.style.opacity = 0.8}
            ></button>
          </div>

          {/* ========== BARRE DE PROGRESSION ========== */}
          <div className="px-4 pt-3 pb-2 bg-light border-bottom">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted fw-semibold">
                <FaCheckCircle className="me-1 text-success" />
                Progression du formulaire
              </small>
              <small className="text-muted fw-bold">{progress}%</small>
            </div>
            <div className="progress" style={{ height: '6px', borderRadius: '3px' }}>
              <div 
                className="progress-bar bg-success" 
                role="progressbar" 
                style={{ width: `${progress}%`, transition: 'width 0.3s ease' }}
                aria-valuenow={progress} 
                aria-valuemin="0" 
                aria-valuemax="100"
              ></div>
            </div>
          </div>

          {/* ========== BODY ========== */}
          <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto', padding: '2rem' }}>
            
            {/* Alerte d'information */}
            <div 
              className="alert alert-primary border-0 mb-4 shadow-sm" 
              style={{ borderLeft: '4px solid #4b6cb7', borderRadius: '8px' }}
            >
              <div className="d-flex">
                <FaInfoCircle className="me-3 mt-1 flex-shrink-0" size={20} />
                <div>
                  <h6 className="alert-heading fw-bold mb-2">
                    <FaHandshake className="me-2" />
                    Guide de saisie
                  </h6>
                  <ul className="mb-0 small" style={{ paddingLeft: '1.2rem' }}>
                    <li className="mb-1">
                      <strong>Co-titulaire :</strong> Utilisez cette option si la personne partage la propriété du compte
                    </li>
                    <li className="mb-1">
                      <strong>Procuration :</strong> Pour autoriser quelqu'un à agir en votre nom
                    </li>
                    <li className="mb-1">
                      <strong>Tutelle/Curatelle :</strong> Mesures de protection juridique
                    </li>
                    <li>
                      <strong>Document requis :</strong> Justificatif légal du mandat (procuration signée, jugement de tutelle, etc.)
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {!selectedDomain?.id && (
              <div className="alert alert-warning border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                <div className="d-flex">
                  <FaExclamationTriangle className="me-2 mt-1" />
                  <div>
                    <strong>Attention :</strong> Aucun domaine sélectionné. Veuillez sélectionner un domaine pour continuer.
                  </div>
                </div>
              </div>
            )}

            {backendErrors.general && (
              <div className="alert alert-danger border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                <div className="d-flex">
                  <FaExclamationTriangle className="me-2 mt-1" />
                  <div>
                    <strong>Erreur :</strong> {backendErrors.general}
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              {representatives.map((representative, index) => (
                <div 
                  key={index} 
                  className="card mb-4 border-0 shadow-sm"
                  style={{ 
                    borderRadius: '12px',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <div 
                    className="card-header text-white d-flex justify-content-between align-items-center"
                    style={{ 
                      background: 'ligth',
                      padding: '1rem 1.5rem'
                    }}
                  >
                    <h6 className="mb-0 fw-bold d-flex align-items-center">
                      <div 
                        className="rounded-circle bg-white text-primary d-flex align-items-center justify-content-center me-2"
                        style={{ width: '32px', height: '32px', fontSize: '14px', fontWeight: 'bold' }}
                      >
                        {index + 1}
                      </div>
                      Mandataire #{index + 1}
                    </h6>

                    {representatives.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-light text-danger fw-semibold"
                        onClick={() => removeRepresentative(index)}
                        style={{ 
                          borderRadius: '20px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#dc3545';
                          e.target.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#f8f9fa';
                          e.target.style.color = '#dc3545';
                        }}
                      >
                        <FaTrash className="me-1" />
                        Supprimer
                      </button>
                    )}
                  </div>

                  <div className="card-body p-4">
                    <div className="row g-4">
                      
                      {/* ========== COMPTE MANDATAIRE ========== */}
                      <div className="col-12">
                        <label className="form-label fw-semibold d-flex align-items-center">
                          <FaUserTie className="me-2 text-primary" />
                          Compte Mandataire 
                          <span className="text-danger ms-1">*</span>
                        </label>

                        <Select
                          options={nonManagedAccounts}
                          isLoading={isLoadingAccounts}
                          onInputChange={setSearchTerm}
                          value={nonManagedAccounts.find(opt =>
                            opt.value === representative.representativeAccountId
                          )}
                          onChange={(selected) => {
                            setValue(
                              `representatives.${index}.representativeAccountId`,
                              selected?.value || null
                            );
                            clearErrors(`representatives.${index}.representativeAccountId`);
                          }}
                          placeholder="🔍 Rechercher par nom, email ou numéro de compte..."
                          noOptionsMessage={() => "Aucun compte trouvé"}
                          loadingMessage={() => "Recherche en cours..."}
                          isDisabled={!selectedDomain?.id}
                          styles={customSelectStyles}
                          error={!!errors?.representatives?.[index]?.representativeAccountId}
                        />

                        {errors?.representatives?.[index]?.representativeAccountId && (
                          <div className="invalid-feedback d-block mt-2">
                            <FaExclamationTriangle className="me-1" />
                            {errors.representatives[index].representativeAccountId.message}
                          </div>
                        )}

                        <small className="form-text text-muted d-block mt-2">
                          💡 Saisissez au moins 3 caractères pour lancer la recherche
                        </small>
                      </div>

                      {/* ========== TYPE DE MANDAT ========== */}
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          Type de mandat 
                          <span className="text-danger ms-1">*</span>
                        </label>

                        <select
                          className={`account-type-select ${
                            errors?.representatives?.[index]?.mandateType ? 'is-invalid' : ''
                          }`}
                          value={representative.mandateType}
                          onChange={(e) => {
                            setValue(`representatives.${index}.mandateType`, e.target.value);
                            clearErrors(`representatives.${index}.mandateType`);

                            if (e.target.value !== 'OTHER') {
                              setValue(`representatives.${index}.otherMandateText`, '');
                              clearErrors(`representatives.${index}.otherMandateText`);
                            }
                          }}
                          disabled={!selectedDomain?.id}
                          style={{ borderRadius: '8px' }}
                        >
                          <option value="">Sélectionner un type de mandat...</option>
                          {mandateTypeOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.icon} {option.label}
                            </option>
                          ))}
                        </select>

                        {errors?.representatives?.[index]?.mandateType && (
                          <div className="invalid-feedback d-block">
                            <FaExclamationTriangle className="me-1" />
                            {errors.representatives[index].mandateType.message}
                          </div>
                        )}

                        {representative.mandateType && representative.mandateType !== 'OTHER' && (
                          <div className="alert alert-info mt-2 py-2 px-3 mb-0 border-0" style={{ borderRadius: '8px', fontSize: '13px' }}>
                            <FaInfoCircle className="me-1" />
                            {mandateTypeOptions.find(o => o.value === representative.mandateType)?.description}
                          </div>
                        )}
                      </div>

                      {/* ========== PRÉCISION MANDAT (SI OTHER) ========== */}
                      {representative.mandateType === 'OTHER' && (
                        <div className="col-md-6">
                          <label className="form-label fw-semibold">
                            Précisez le type de mandat 
                            <span className="text-danger ms-1">*</span>
                          </label>

                          <input
                            type="text"
                            className={`form-control ${
                              errors?.representatives?.[index]?.otherMandateText ? 'is-invalid' : ''
                            }`}
                            placeholder="Ex: Co-titularité, Gestion patrimoniale..."
                            value={representative.otherMandateText || ''}
                            onChange={(e) => {
                              setValue(`representatives.${index}.otherMandateText`, e.target.value);
                              clearErrors(`representatives.${index}.otherMandateText`);
                            }}
                            disabled={!selectedDomain?.id}
                            style={{ borderRadius: '8px' }}
                          />

                          {errors?.representatives?.[index]?.otherMandateText && (
                            <div className="invalid-feedback d-block">
                              <FaExclamationTriangle className="me-1" />
                              {errors.representatives[index].otherMandateText.message}
                            </div>
                          )}
                        </div>
                      )}

                      {/* ========== RELATION ========== */}
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          Relation avec le titulaire 
                          <span className="text-danger ms-1">*</span>
                        </label>

                        <select
                          className={`account-type-select ${
                            errors?.representatives?.[index]?.relationshipType ? 'is-invalid' : ''
                          }`}
                          value={representative.relationshipType}
                          onChange={(e) => {
                            setValue(`representatives.${index}.relationshipType`, e.target.value);
                            clearErrors(`representatives.${index}.relationshipType`);

                            if (e.target.value !== 'OTHER') {
                              setValue(`representatives.${index}.otherRelationshipText`, '');
                              clearErrors(`representatives.${index}.otherRelationshipText`);
                            }
                          }}
                          disabled={!selectedDomain?.id}
                          style={{ borderRadius: '8px' }}
                        >
                          <option value="">Sélectionner une relation...</option>
                          {relationshipTypeOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.icon} {option.label}
                            </option>
                          ))}
                        </select>

                        {errors?.representatives?.[index]?.relationshipType && (
                          <div className="invalid-feedback d-block">
                            <FaExclamationTriangle className="me-1" />
                            {errors.representatives[index].relationshipType.message}
                          </div>
                        )}

                        {representative.relationshipType && representative.relationshipType !== 'OTHER' && (
                          <div className="alert alert-info mt-2 py-2 px-3 mb-0 border-0" style={{ borderRadius: '8px', fontSize: '13px' }}>
                            <FaInfoCircle className="me-1" />
                            {relationshipTypeOptions.find(o => o.value === representative.relationshipType)?.description}
                          </div>
                        )}
                      </div>

                      {/* ========== PRÉCISION RELATION (SI OTHER) ========== */}
                      {representative.relationshipType === 'OTHER' && (
                        <div className="col-md-6">
                          <label className="form-label fw-semibold">
                            Précisez la relation 
                            <span className="text-danger ms-1">*</span>
                          </label>

                          <input
                            type="text"
                            className={`form-control ${
                              errors?.representatives?.[index]?.otherRelationshipText ? 'is-invalid' : ''
                            }`}
                            placeholder="Ex: Associé, Conseiller financier..."
                            value={representative.otherRelationshipText || ''}
                            onChange={(e) => {
                              setValue(`representatives.${index}.otherRelationshipText`, e.target.value);
                              clearErrors(`representatives.${index}.otherRelationshipText`);
                            }}
                            disabled={!selectedDomain?.id}
                            style={{ borderRadius: '8px' }}
                          />

                          {errors?.representatives?.[index]?.otherRelationshipText && (
                            <div className="invalid-feedback d-block">
                              <FaExclamationTriangle className="me-1" />
                              {errors.representatives[index].otherRelationshipText.message}
                            </div>
                          )}
                        </div>
                      )}

                      {/* ========== DOCUMENT DE PREUVE ========== */}
                      <div className="col-12">
                        <label className="form-label fw-semibold d-flex align-items-center">
                          <FaFileUpload className="me-2 text-primary" />
                          Document justificatif 
                          <span className="text-danger ms-1">*</span>
                        </label>

                        <div 
                          className={`position-relative ${
                            errors?.representatives?.[index]?.mandateProofFile ? 'border-danger' : ''
                          }`}
                          style={{ borderRadius: '8px' }}
                        >
                          <input
                            type="file"
                            className={`form-control ${
                              errors?.representatives?.[index]?.mandateProofFile ? 'is-invalid' : ''
                            }`}
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            onChange={(e) => {
                              const file = e.target.files?.[0] || null;
                              setValue(`representatives.${index}.mandateProofFile`, file);
                              clearErrors(`representatives.${index}.mandateProofFile`);
                            }}
                            disabled={!selectedDomain?.id}
                            style={{ 
                              borderRadius: '8px',
                              padding: '0.75rem'
                            }}
                          />

                          {representative.mandateProofFile && (
                            <div className="mt-2 alert alert-success py-2 px-3 mb-0 border-0 d-flex align-items-center" style={{ borderRadius: '8px' }}>
                              <FaCheckCircle className="me-2" />
                              <span className="small">
                                <strong>{representative.mandateProofFile.name}</strong> 
                                {' '}({(representative.mandateProofFile.size / 1024 / 1024).toFixed(2)} MB)
                              </span>
                            </div>
                          )}
                        </div>

                        {errors?.representatives?.[index]?.mandateProofFile && (
                          <div className="invalid-feedback d-block mt-2">
                            <FaExclamationTriangle className="me-1" />
                            {errors.representatives[index].mandateProofFile.message}
                          </div>
                        )}

                        <div className="alert alert-light mt-2 py-2 px-3 mb-0 border" style={{ borderRadius: '8px', fontSize: '13px' }}>
                          <FaInfoCircle className="me-1 text-info" />
                          <strong>Documents acceptés :</strong> Procuration signée, jugement de tutelle, acte de naissance (pour parent), contrat de co-titularité, etc.
                          <br />
                          <strong>Formats :</strong> PDF, JPG, PNG, DOC, DOCX • <strong>Taille max :</strong> 10 MB
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              ))}

              {/* ========== BOUTON AJOUTER MANDATAIRE ========== */}
              <div className="text-center mb-4">
                <button
                  type="button"
                  className="btn btn-outline-light btn-lg px-5"
                  onClick={addRepresentative}
                  disabled={!selectedDomain?.id}
                  style={{ 
                    borderRadius: '25px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(75, 108, 183, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <FaPlus className="me-2" />
                  Ajouter un Mandataire
                </button>
              </div>

            </form>
          </div>

          {/* ========== FOOTER AMÉLIORÉ ========== */}
          <div className="modal-footer border-top-0 bg-light" style={{ padding: '1.5rem 2rem' }}>
            <div className="d-flex justify-content-between align-items-center w-100">
              <button
                type="button"
                className="btn btn-outline-secondary btn-lg px-4"
                onClick={onClose}
                style={{ borderRadius: '25px', fontWeight: '600' }}
              >
                Annuler
              </button>

              <div className="d-flex align-items-center gap-3">
                <div className="text-end">
                  <small className="text-muted d-block">
                    {representatives.length} mandataire{representatives.length > 1 ? 's' : ''}
                  </small>
                  <small className="text-success fw-semibold">
                    Formulaire complété à {progress}%
                  </small>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-lg px-5"
                  onClick={handleSubmit(onSubmit)}
                  disabled={!selectedDomain?.id || representatives.length === 0 || progress < 100}
                  style={{ 
                    borderRadius: '25px',
                    fontWeight: '600',
                    background: progress === 100 
                      ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' 
                      : 'linear-gradient(135deg, #4b6cb7 0%, #182848 100%)',
                    border: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (progress === 100) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(40, 167, 69, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <FaCheckCircle className="me-2" />
                  {progress === 100 ? '✅ Valider' : `⏳ Compléter (${progress}%)`}
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AddLegalRepresentativesModal;